import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  ACADEMY_QUIZ_OPTIONS,
  ACADEMY_SEARCH_PARAM,
} from "./RentmatchAcademy.constants";
import {
  TAcademyQuizAnswers,
  TAcademyQuizOption,
} from "./RentmatchAcademy.types";

// Kept in the browser only (Jonas, DEV-3526): a broker who works through the
// academy in parts keeps their progress, without anything stored on the account.
const QUIZ_ANSWERS_STORAGE_KEY = "rentio-dashboard:rentmatch-academy:quiz";

const isQuizOption = (value: unknown): value is TAcademyQuizOption =>
  ACADEMY_QUIZ_OPTIONS.includes(value as TAcademyQuizOption);

const readQuizAnswers = (): TAcademyQuizAnswers => {
  try {
    const raw = window.localStorage.getItem(QUIZ_ANSWERS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => isQuizOption(value)),
    ) as TAcademyQuizAnswers;
  } catch {
    // Storage blocked (a private window) or a malformed value: start empty.
    return {};
  }
};

const writeQuizAnswers = (answers: TAcademyQuizAnswers) => {
  try {
    window.localStorage.setItem(
      QUIZ_ANSWERS_STORAGE_KEY,
      JSON.stringify(answers),
    );
  } catch {
    // Storage blocked: the score still counts on screen, it is just not kept.
  }
};

export const useAcademyQuizAnswers = () => {
  const [answers, setAnswers] =
    React.useState<TAcademyQuizAnswers>(readQuizAnswers);

  const setAnswer = (key: string, option: TAcademyQuizOption) =>
    setAnswers(current => {
      const next = { ...current, [key]: option };
      writeQuizAnswers(next);

      return next;
    });

  return { answers, setAnswer };
};

// The search query lives in the address, so a result opened from the overview
// can highlight it and the back button returns to the same results.
export const useAcademySearchQuery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get(ACADEMY_SEARCH_PARAM) ?? "";

  const setQuery = (value: string) =>
    setSearchParams(
      current => {
        const next = new URLSearchParams(current);
        if (value) {
          next.set(ACADEMY_SEARCH_PARAM, value);
        } else {
          next.delete(ACADEMY_SEARCH_PARAM);
        }

        return next;
      },
      // Typing replaces the entry instead of adding one per key stroke.
      { replace: true },
    );

  return { query, setQuery, searchParams };
};
